/**
 * Nova Trust Center — product marketing page.
 *
 * Story arc: Hero → Live Product → Problem → Features → AI Governance
 * → Build in Minutes → Access Requests → Why Nova → Integrations
 * → Gallery → Final CTA.
 *
 * Every capability claim is grounded in what the Nova Trust Center product
 * ships today. See /app/memory/PRD.md for the full truth audit.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Repeat,
  FolderOpen,
  Zap,
  Sparkles,
  ShieldCheck,
  Lock,
  FileText,
  ClipboardCheck,
  Rocket,
  Bot,
  ShieldAlert,
  Scale,
  Handshake,
  Building2,
  Palette,
  Layers as LayersIcon,
  BookOpen,
  Inbox,
  Eye,
  Send,
} from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import BookDemoModal from './BookDemoModal';

// ---- Assets (real Nova product screenshots from curated Kestrel demo) ----
import shotOverviewHero from '../../assets/trust-marketing/01a_overview_hero_crop.png';
import shotOverview from '../../assets/trust-marketing/01_overview.png';
import shotSecurity from '../../assets/trust-marketing/06_security.png';
import shotControls from '../../assets/trust-marketing/03_controls.png';
import shotSubprocessors from '../../assets/trust-marketing/08_subprocessors.png';
import shotFaq from '../../assets/trust-marketing/09_faq.png';
import shotContact from '../../assets/trust-marketing/10_contact.png';
import shotAiGov from '../../assets/trust-marketing/05_ai_governance.png';

// ---- Admin Builder — real screenshots captured from the /trust-center admin ----
import adminBranding from '../../assets/trust-marketing/admin-builder/branding.jpg';
import adminSections from '../../assets/trust-marketing/admin-builder/sections.jpg';
import adminDocuments from '../../assets/trust-marketing/admin-builder/documents.jpg';
import adminRequests from '../../assets/trust-marketing/admin-builder/requests.jpg';
import adminPreview from '../../assets/trust-marketing/admin-builder/preview.jpg';
import adminPublish from '../../assets/trust-marketing/admin-builder/publish.jpg';

// ---- Design tokens ----
const INK = '#0A0A0A';
const MUTED = '#475569';
const HAIR = '#E2E8F0';
const ACCENT = '#0F62FE';
const CANVAS = '#F8FAFB';

// ---- Book Demo modal context ----
// Any component can open the modal by calling useBookDemo().open('source-tag').
const BookDemoCtx = createContext({ open: () => {}, close: () => {} });
const useBookDemo = () => useContext(BookDemoCtx);

// ---- Motion helpers ----
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.55, ease: [0.2, 0.65, 0.3, 1], delay },
});

const Section = ({ id, children, className = '', variant = 'light' }) => (
  <section
    id={id}
    className={`relative w-full ${
      variant === 'dark'
        ? 'bg-[#0A0A0A] text-white dark:bg-[#050505]'
        : 'bg-transparent text-[#0A0A0A] dark:text-[#F5F5F5]'
    } ${className}`}
  >
    <div className="max-w-7xl mx-auto px-6 py-20 sm:py-24 md:py-28">
      {children}
    </div>
  </section>
);

const Eyebrow = ({ children, tone = 'default' }) => (
  <div
    className={`text-[11px] font-label font-semibold uppercase tracking-[0.25em] ${
      tone === 'dark'
        ? 'text-white/60'
        : 'text-[#64748B] dark:text-white/60'
    }`}
    data-testid="trust-marketing-eyebrow"
  >
    {children}
  </div>
);

const H2 = ({ children, className = '', dark = false }) => (
  <h2
    className={`font-display font-semibold tracking-tight leading-[1.1] text-3xl md:text-4xl ${
      dark ? 'text-white' : 'text-[#0A0A0A] dark:text-[#F5F5F5]'
    } ${className}`}
  >
    {children}
  </h2>
);

const Sub = ({ children, className = '', dark = false }) => (
  <p
    className={`mt-4 font-body text-base md:text-lg leading-relaxed max-w-[62ch] ${
      dark ? 'text-white/70' : 'text-[#475569] dark:text-white/70'
    } ${className}`}
  >
    {children}
  </p>
);

// ---- CTAs ----
// Renders as a <button> when onClick is provided, otherwise as an <a>. This
// lets us drop the same visual into either an anchor (external link) or the
// Book-a-demo modal trigger without a wrapper component.
const PrimaryCTA = ({ children, testid, onClick, ...rest }) => {
  const cls =
    'inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-[#0F62FE] text-white text-[14px] font-semibold hover:bg-[#0A4FCC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/40 focus-visible:ring-offset-2 transition-colors';
  if (onClick) {
    return (
      <button type="button" onClick={onClick} data-testid={testid} className={cls} {...rest}>
        {children}
      </button>
    );
  }
  return (
    <a {...rest} data-testid={testid} className={cls}>
      {children}
    </a>
  );
};

const SecondaryCTA = ({ children, testid, dark = false, onClick, ...rest }) => {
  const cls = `inline-flex items-center gap-1.5 h-11 px-4 rounded-full text-[14px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
    dark
      ? 'text-white hover:bg-white/5 focus-visible:ring-white/40'
      : 'text-[#0A0A0A] dark:text-[#F5F5F5] hover:bg-[#0F172A]/[0.04] dark:hover:bg-white/[0.06] focus-visible:ring-[#0F62FE]/40'
  }`;
  if (onClick) {
    return (
      <button type="button" onClick={onClick} data-testid={testid} className={cls}>
        {children}
      </button>
    );
  }
  return (
    <a {...rest} data-testid={testid} className={cls}>
      {children}
    </a>
  );
};

// =============================================================================
// §1 — HERO
// =============================================================================
const Hero = () => {
  const publicPath = '/t/workspace-1';
  const navigate = useNavigate();
  return (
    <section id="hero" className="relative w-full overflow-hidden bg-[#D4E0DA]/55 dark:bg-[#101F18]/55 border-b border-black/10 dark:border-white/10 min-h-[480px] md:min-h-[640px]" data-testid="trust-marketing-hero">
      <div className="max-w-7xl mx-auto px-6 pt-24 md:pt-32 pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto text-center">
          <div className="relative z-10">
            <motion.div {...fadeUp(0)}>
              <p className="font-label text-[11px] uppercase tracking-[0.3em] text-black/55 dark:text-white/55" data-testid="trust-marketing-eyebrow">Nova Trust Center</p>
            </motion.div>
            <motion.h1
              {...fadeUp(0.08)}
              className="mt-8 font-display font-semibold tracking-tight leading-[1.25] text-2xl sm:text-3xl lg:text-4xl text-[#0A0A0A] dark:text-[#F5F5F5]"
            >
              Build trust before your
              <br className="hidden sm:block" /> prospects have to ask
            </motion.h1>
            <motion.p
              {...fadeUp(0.16)}
              className="mt-8 font-body text-base md:text-lg leading-relaxed max-w-2xl mx-auto text-black/70 dark:text-white/70"
            >
              Publish your compliance posture, security practices, privacy program, AI
              governance, and subprocessors — all on one branded page.
            </motion.p>
            <motion.div {...fadeUp(0.24)} className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => navigate('/register')}
                data-testid="trust-marketing-hero-start-trial"
                className="font-label bg-[#0F62FE] dark:bg-[#2E79FF] text-white px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] shadow-sm hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] hover:shadow-md transition-all duration-300"
              >
                Start Free Trial
              </button>
              <button
                type="button"
                onClick={() => navigate('/pricing')}
                data-testid="trust-marketing-hero-view-pricing"
                className="font-label border border-[#0A0A0A]/20 dark:border-white/25 text-[#0A0A0A] dark:text-[#F5F5F5] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:border-[#0A0A0A] dark:hover:border-white/30 hover:bg-[#D9CFD9]/40 dark:hover:bg-[#1E1724]/40 transition-all duration-500"
              >
                View Pricing
              </button>
            </motion.div>
          </div>
        </div>

        {/* Framework proof strip */}
        <motion.div {...fadeUp(0.32)} className="mt-16 sm:mt-20 border-t border-[#E2E8F0] dark:border-white/10 pt-6">
          <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.18em] text-[#94A3B8] dark:text-white/50 mb-3 text-center">
            Publish the frameworks your customers ask about
          </div>
          <ul
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[13px] font-semibold text-black/70 dark:text-white/70"
            data-testid="trust-marketing-framework-strip"
          >
            {['SOC 2', 'ISO 27001', 'GDPR', 'HIPAA', 'PCI DSS'].map((f, idx, arr) => (
              <li key={f} className="inline-flex items-center">
                <span className="tracking-[0.06em]">{f}</span>
                {idx < arr.length - 1 && (
                  <span aria-hidden className="mx-3 sm:mx-4 text-[#CBD5E1] dark:text-white/25">·</span>
                )}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
};

// =============================================================================
// §1b — WHO IT'S FOR (compact qualifier row)
// =============================================================================
const WhoItsFor = () => {
  const rows = [
    {
      icon: ShieldAlert,
      title: 'Security teams',
      body: 'Publish once. Deflect the questionnaire backlog.',
    },
    {
      icon: Scale,
      title: 'Compliance teams',
      body: 'Show framework alignment and evidence, versioned.',
    },
    {
      icon: Handshake,
      title: 'Sales & Solutions Engineering',
      body: 'A link that unblocks security reviews mid-deal.',
    },
    {
      icon: Building2,
      title: 'B2B SaaS leaders',
      body: 'Look enterprise-ready from day one.',
    },
  ];
  return (
    <section
      id="who-its-for"
      className="w-full border-t border-[#E2E8F0] dark:border-white/10"
      data-testid="trust-marketing-who-its-for"
    >
      <div className="max-w-7xl mx-auto px-6 py-12 sm:py-14">
        <motion.div {...fadeUp(0)} className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
          <div>
            <Eyebrow>Who it&rsquo;s for</Eyebrow>
            <h3 className="mt-1.5 font-display font-semibold text-xl md:text-2xl tracking-tight text-[#0A0A0A] dark:text-[#F5F5F5]">
              Built for the four teams a security review touches.
            </h3>
          </div>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {rows.map((r, i) => (
            <motion.div
              key={r.title}
              {...fadeUp(0.05 + i * 0.05)}
              className="rounded-xl border border-[#E2E8F0] dark:border-white/10 bg-white dark:bg-white/[0.04] p-4"
              data-testid={`trust-marketing-audience-${i}`}
            >
              <r.icon className="w-4 h-4 text-[#0F62FE]" strokeWidth={2.25} />
              <div className="mt-2.5 text-[14px] font-semibold text-[#0A0A0A] dark:text-[#F5F5F5]">{r.title}</div>
              <p className="mt-1 text-[12.5px] text-[#475569] dark:text-white/70 leading-relaxed">{r.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// §2 — LIVE PRODUCT PANEL (6 real tabs, user-driven)
// =============================================================================
const TABS = [
  { key: 'overview', label: 'Overview', src: shotOverview, eager: true,
    alt: 'Kestrel Trust Center overview page with trust-at-a-glance signals and questionnaire coverage' },
  { key: 'security', label: 'Security', src: shotSecurity, eager: false,
    alt: 'Security posture page covering identity & access, encryption, vulnerability management, and data privacy' },
  { key: 'compliance', label: 'Compliance', src: shotControls, eager: false,
    alt: 'Compliance page showing SOC 2, GDPR, ISO 27001, and PCI DSS framework alignment with evidence behind each' },
  { key: 'subprocessors', label: 'Subprocessors', src: shotSubprocessors, eager: false,
    alt: 'Subprocessors list showing vendors, purpose, and regions' },
  { key: 'faq', label: 'FAQ', src: shotFaq, eager: false,
    alt: 'FAQ page with categorised and searchable answers' },
  { key: 'contact', label: 'Contact', src: shotContact, eager: false,
    alt: 'Contact page with security team channels and responsible disclosure' },
];

const LiveProductPanel = () => {
  const [active, setActive] = useState('overview');
  const [loaded, setLoaded] = useState({ overview: true });

  // Preload the neighbours of the active tab so switching feels instant,
  // without loading all six on first paint.
  useEffect(() => {
    const idx = TABS.findIndex((t) => t.key === active);
    [idx - 1, idx + 1].forEach((i) => {
      if (i >= 0 && i < TABS.length) {
        const k = TABS[i].key;
        if (!loaded[k]) setLoaded((s) => ({ ...s, [k]: true }));
      }
    });
  }, [active, loaded]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const idx = TABS.findIndex((t) => t.key === active);
      setActive(TABS[(idx + 1) % TABS.length].key);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const idx = TABS.findIndex((t) => t.key === active);
      setActive(TABS[(idx - 1 + TABS.length) % TABS.length].key);
    }
  };

  return (
    <Section id="product">
      <motion.div {...fadeUp(0)}>
        <Eyebrow>Live product</Eyebrow>
        <H2 className="mt-3 max-w-[22ch]">See exactly what your customers see.</H2>
      </motion.div>

      <motion.div {...fadeUp(0.1)} className="mt-10">
        <div
          role="tablist"
          aria-label="Kestrel Trust Center product pages"
          onKeyDown={onKeyDown}
          className="flex flex-wrap items-center gap-1.5 mb-4"
          data-testid="trust-marketing-live-product-tabs"
        >
          {TABS.map((t) => {
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                data-testid={`trust-marketing-tab-${t.key}`}
                onClick={() => setActive(t.key)}
                className={`h-9 px-4 rounded-full text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/40 ${
                  isActive
                    ? 'bg-[#0A0A0A] text-white dark:bg-[#F5F5F5] dark:text-[#0A0A0A]'
                    : 'text-[#475569] dark:text-white/70 hover:text-[#0A0A0A] dark:hover:text-white hover:bg-[#F1F5F9] dark:hover:bg-white/[0.06]'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div
          role="tabpanel"
          aria-labelledby={`trust-marketing-tab-${active}`}
          className="rounded-2xl overflow-hidden border border-[#E2E8F0] dark:border-white/10 bg-white shadow-[0_30px_60px_-30px_rgba(15,23,42,0.22),0_8px_20px_-8px_rgba(15,23,42,0.08)] dark:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6),0_8px_20px_-8px_rgba(0,0,0,0.5)]"
          data-testid="trust-marketing-live-product-panel"
        >
          <div className="relative aspect-[1440/900] bg-[#FAFAFB]">
            {TABS.map((t) => (
              (loaded[t.key] || t.eager) && (
                <img
                  key={t.key}
                  src={t.src}
                  alt={t.alt}
                  loading={t.eager ? 'eager' : 'lazy'}
                  decoding="async"
                  className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-300 ${
                    active === t.key ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                />
              )
            ))}
          </div>
        </div>
      </motion.div>
    </Section>
  );
};

// =============================================================================
// §3 — PROBLEM (3 cards, concise, qualitative)
// =============================================================================
// =============================================================================
// §3 (former Problem section merged into WhyNova below)
// =============================================================================


// =============================================================================
// §4 — WHAT YOU CAN DO WITH NOVA TRUST CENTER (5 outcome-oriented cards)
// =============================================================================
const FeatureGrid = () => {
  // Outcome-first framing: "what can you do", not "what objects exist".
  // Card #4 (Documents & Access) folds in the former Access Requests section
  // via a small inline Request → Review → Approve strip — enough to teach the
  // workflow without demanding a full-width dedicated section.
  const cards = [
    {
      key: 'compliance',
      icon: ShieldCheck,
      title: 'Compliance & Frameworks',
      body: 'Publish alignment with SOC 2, ISO 27001, GDPR, HIPAA, and PCI DSS on one page — with the controls behind them.',
    },
    {
      key: 'security',
      icon: Lock,
      title: 'Security & Privacy',
      body: 'Show how you protect customer data, handle regional privacy laws, and disclose subprocessors.',
    },
    {
      key: 'ai-gov',
      icon: Bot,
      title: 'AI Governance',
      body: 'Publish your AI usage, human-oversight practices, and governance policies as a first-class category.',
    },
    {
      key: 'documents',
      icon: FileText,
      title: 'Documents & Access',
      body: 'Make some documents public. Gate others behind a lightweight access request your team reviews.',
      workflow: ['Request', 'Review', 'Approve'],
    },
    {
      key: 'brand',
      icon: Sparkles,
      title: 'Brand & Customize',
      body: 'Match your logo, colors, and voice. Ship a Trust Center that reads as part of your product, not a template.',
    },
  ];
  return (
    <Section id="features" className="border-t border-[#E2E8F0] dark:border-white/10">
      <motion.div {...fadeUp(0)} className="max-w-3xl">
        <Eyebrow>What you can do</Eyebrow>
        <H2 className="mt-3 max-w-[24ch]">Everything a buyer wants to check — in one place.</H2>
        <Sub>
          Nova turns the messy work of proving trust into a single, branded surface your team
          maintains and your buyers can browse anytime.
        </Sub>
      </motion.div>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {cards.map((c, i) => (
          <motion.div
            key={c.key}
            {...fadeUp(0.03 + i * 0.05)}
            className={`group rounded-2xl border border-[#E2E8F0] dark:border-white/10 bg-white dark:bg-white/[0.04] p-6 hover:border-[#0F62FE]/30 dark:hover:border-[#0F62FE]/50 hover:shadow-[0_18px_40px_-24px_rgba(15,98,254,0.28)] transition-all duration-300 ${
              // Give card #5 a subtle span so 5 cards balance in a 3-col grid
              i === 4 ? 'md:col-span-2 lg:col-span-1' : ''
            }`}
            data-testid={`trust-marketing-feature-${c.key}`}
          >
            <div className="w-10 h-10 rounded-lg bg-[#F0F6FF] dark:bg-[#0F62FE]/15 border border-[#DBEAFE] dark:border-[#0F62FE]/30 inline-flex items-center justify-center">
              <c.icon className="w-4.5 h-4.5 text-[#0F62FE]" strokeWidth={2.25} />
            </div>
            <div className="mt-4 text-[16px] font-semibold text-[#0A0A0A] dark:text-[#F5F5F5]">{c.title}</div>
            <p className="mt-1.5 text-[13.5px] text-[#475569] dark:text-white/70 leading-relaxed">{c.body}</p>
            {c.workflow && (
              <div
                className="mt-4 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0F62FE]"
                data-testid={`trust-marketing-feature-${c.key}-workflow`}
              >
                {c.workflow.map((step, idx) => (
                  <React.Fragment key={step}>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-4 h-4 rounded-full bg-[#0F62FE] text-white text-[9px] leading-none inline-flex items-center justify-center">
                        {idx + 1}
                      </span>
                      {step}
                    </span>
                    {idx < c.workflow.length - 1 && (
                      <span aria-hidden className="text-[#0F62FE]/40">→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

// =============================================================================
// §5 — AI GOVERNANCE (dedicated)
// =============================================================================
const AiGovernance = () => (
  <Section id="ai-governance" className="border-t border-[#E2E8F0] dark:border-white/10">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-8 lg:gap-x-12 items-center">
      <motion.div {...fadeUp(0)} className="lg:col-span-5">
        <Eyebrow>The differentiator</Eyebrow>
        <H2 className="mt-3 max-w-[18ch]">AI governance, as a first-class category.</H2>
        <Sub>
          Most Trust Centers bolt AI on as an afterthought. Nova publishes AI usage, human
          oversight, and governance policies alongside compliance — where enterprise buyers now
          expect to find them.
        </Sub>
        <ul className="mt-6 space-y-3">
          {[
            { t: 'Overview & policies', d: 'Publish how you approach AI plus the governance policies behind it.' },
            { t: 'Transparency & controls', d: 'Human-oversight practices and data-handling controls, browsable by buyers or gated by request.' },
          ].map((row) => (
            <li key={row.t} className="flex gap-3">
              <CheckCircle2 className="w-4 h-4 text-[#0F62FE] shrink-0 mt-1" strokeWidth={2.25} />
              <div>
                <div className="text-[14px] font-semibold text-[#0A0A0A] dark:text-[#F5F5F5]">{row.t}</div>
                <div className="text-[13px] text-[#475569] dark:text-white/70 leading-relaxed">{row.d}</div>
              </div>
            </li>
          ))}
        </ul>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 24 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.7, ease: [0.2, 0.65, 0.3, 1] }}
        className="lg:col-span-7"
      >
        <div className="rounded-2xl overflow-hidden border border-[#E2E8F0] dark:border-white/10 bg-white shadow-[0_30px_60px_-30px_rgba(15,23,42,0.22)] dark:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]">
          <img
            src={shotAiGov}
            alt="Kestrel Trust Center — the Resources page filtered to the AI Governance category, listing AI Usage, Human Oversight, Customer Data Protection, and related resources"
            className="block w-full h-auto"
            loading="lazy"
            decoding="async"
          />
        </div>
      </motion.div>
    </div>
  </Section>
);

// =============================================================================
// §6 — BUILD IN MINUTES (real Admin Builder screenshots, tabbed)
// =============================================================================
const ADMIN_TABS = [
  {
    key: 'branding',
    label: 'Branding',
    icon: Palette,
    src: adminBranding,
    caption: 'Company name, public slug, logo, and accent color — set once, applied everywhere.',
    alt: 'Nova Trust Center admin — Basics tab showing Identity & branding form with company name, public URL slug, logo URL, and accent color',
  },
  {
    key: 'sections',
    label: 'Sections',
    icon: LayersIcon,
    src: adminSections,
    caption: 'Edit the trust statement, hero copy, and toggle which public sections appear.',
    alt: 'Nova Trust Center admin — Overview tab showing trust statement editor, hero fields, AI governance statement, and section visibility toggles',
  },
  {
    key: 'documents',
    label: 'Documents',
    icon: BookOpen,
    src: adminDocuments,
    caption: 'Publish, version, and route resources — each with access level and framework tags.',
    alt: 'Nova Trust Center admin — Resources tab listing published documents like Encryption with access type, framework, effective and next-review dates',
  },
  {
    key: 'requests',
    label: 'Access requests',
    icon: Inbox,
    src: adminRequests,
    caption: 'Review documentation requests from your public page and approve or reject them.',
    alt: 'Nova Trust Center admin — Requests tab showing the incoming documentation-request queue',
  },
  {
    key: 'preview',
    label: 'Preview',
    icon: Eye,
    src: adminPreview,
    caption: 'Full-screen desktop/tablet/mobile preview of the exact public page.',
    alt: 'Nova Trust Center admin — Preview modal showing the Kestrel public Trust Center as buyers see it',
  },
  {
    key: 'publish',
    label: 'Publish',
    icon: Send,
    src: adminPublish,
    caption: 'A readiness score plus one-click publish/unpublish — no implementation team required.',
    alt: 'Nova Trust Center admin — top-of-page publish bar with live status, readiness score, and required-item checklist',
  },
];

const BuildInMinutes = () => {
  const [active, setActive] = useState('branding');
  const [loaded, setLoaded] = useState({ branding: true });

  useEffect(() => {
    const idx = ADMIN_TABS.findIndex((t) => t.key === active);
    // Ensure the active tab plus its immediate neighbours are loaded so
    // switching feels instant without paying for all six on first paint.
    const toLoad = [idx, idx - 1, idx + 1].filter((i) => i >= 0 && i < ADMIN_TABS.length);
    const missing = toLoad
      .map((i) => ADMIN_TABS[i].key)
      .filter((k) => !loaded[k]);
    if (missing.length) {
      setLoaded((s) => {
        const next = { ...s };
        missing.forEach((k) => { next[k] = true; });
        return next;
      });
    }
  }, [active, loaded]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const idx = ADMIN_TABS.findIndex((t) => t.key === active);
      setActive(ADMIN_TABS[(idx + 1) % ADMIN_TABS.length].key);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const idx = ADMIN_TABS.findIndex((t) => t.key === active);
      setActive(ADMIN_TABS[(idx - 1 + ADMIN_TABS.length) % ADMIN_TABS.length].key);
    }
  };

  const activeTab = ADMIN_TABS.find((t) => t.key === active) || ADMIN_TABS[0];

  return (
    <Section id="build" className="border-t border-[#E2E8F0] dark:border-white/10">
      <motion.div {...fadeUp(0)} className="max-w-3xl">
        <Eyebrow>The admin, for real</Eyebrow>
        <H2 className="mt-3 max-w-[22ch]">Draft, preview, publish — from one screen.</H2>
        <Sub>
          Every tab below is a real screenshot from the Nova admin. Click through to see exactly
          what you&rsquo;ll use after you sign up — no fictional UI, no implementation team required.
        </Sub>
      </motion.div>

      <motion.div {...fadeUp(0.08)} className="mt-10">
        <div className="flex items-center gap-2 mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#64748B] dark:text-white/60">
          <ClipboardCheck className="w-3.5 h-3.5" /> Admin builder
        </div>

        <div
          role="tablist"
          aria-label="Nova Trust Center admin builder"
          onKeyDown={onKeyDown}
          className="flex flex-wrap items-center gap-1.5 mb-4"
          data-testid="trust-marketing-admin-tabs"
        >
          {ADMIN_TABS.map((t) => {
            const isActive = active === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                data-testid={`trust-marketing-admin-tab-${t.key}`}
                onClick={() => setActive(t.key)}
                className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/40 ${
                  isActive
                    ? 'bg-[#0A0A0A] text-white dark:bg-[#F5F5F5] dark:text-[#0A0A0A]'
                    : 'text-[#475569] dark:text-white/70 hover:text-[#0A0A0A] dark:hover:text-white hover:bg-[#F1F5F9] dark:hover:bg-white/[0.06]'
                }`}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div
          role="tabpanel"
          aria-labelledby={`trust-marketing-admin-tab-${active}`}
          className="rounded-2xl overflow-hidden border border-[#E2E8F0] dark:border-white/10 bg-white shadow-[0_30px_60px_-30px_rgba(15,23,42,0.22),0_8px_20px_-8px_rgba(15,23,42,0.08)] dark:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6),0_8px_20px_-8px_rgba(0,0,0,0.5)]"
          data-testid="trust-marketing-admin-panel"
        >
          <div className="relative aspect-[1600/1000] bg-[#FAFAFB]">
            {ADMIN_TABS.map((t) => (
              (loaded[t.key] || t.key === 'branding') && (
                <img
                  key={t.key}
                  src={t.src}
                  alt={t.alt}
                  loading={t.key === 'branding' ? 'eager' : 'lazy'}
                  decoding="async"
                  className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-300 ${
                    active === t.key ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                />
              )
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-start justify-between gap-4">
          <p
            key={activeTab.key}
            className="text-[13.5px] text-[#475569] dark:text-white/70 max-w-[60ch] leading-relaxed"
            data-testid="trust-marketing-admin-caption"
          >
            <span className="font-semibold text-[#0A0A0A] dark:text-[#F5F5F5]">{activeTab.label}.</span>{' '}
            {activeTab.caption}
          </p>
          <span className="shrink-0 text-[12px] text-[#94A3B8] dark:text-white/50 pt-0.5">
            Real admin · workspace-1
          </span>
        </div>
      </motion.div>
    </Section>
  );
};

// =============================================================================
// §7 — DOCUMENT ACCESS REQUESTS
// =============================================================================
// =============================================================================
// §7 (former Document Access Requests — folded into Features §4)
// =============================================================================


// =============================================================================
// §8 — WHY NOVA (6 differentiators, truth-audited)
// =============================================================================
// =============================================================================
// §3 — WHY NOVA (merged Problem + Why Nova — pain → outcome)
// =============================================================================
const WhyNova = () => {
  // Each card pairs the buyer's pain (top line) with the outcome Nova delivers
  // (body). Kills the previous pain-then-gain duplication while preserving both
  // framings in one scannable place.
  const cards = [
    {
      icon: Repeat,
      pain: 'Stop re-answering the same questionnaires',
      body: 'A public Trust Center answers 80% of security questions before a buyer ever asks.',
    },
    {
      icon: FolderOpen,
      pain: 'End scattered documents and stale copies',
      body: 'Policies, DPAs, and certifications live in one page — versioned and always current.',
    },
    {
      icon: Zap,
      pain: 'Keep security shipping real work',
      body: 'Deflect repetitive buyer requests so your team stays on the roadmap, not the inbox.',
    },
    {
      icon: Rocket,
      pain: 'Look enterprise-ready from day one',
      body: 'A branded, responsive Trust Center that reads as premium — no implementation team required.',
    },
  ];
  return (
    <Section id="why-nova" className="border-t border-[#E2E8F0] dark:border-white/10">
      <motion.div {...fadeUp(0)}>
        <Eyebrow>Why Nova</Eyebrow>
        <H2 className="mt-3 max-w-[22ch]">Turn security reviews from a blocker into a proof point.</H2>
        <Sub>
          Every prospect asks the same questions. Nova puts the answers on one branded page — so
          your team stops re-answering and your deals stop waiting.
        </Sub>
      </motion.div>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {cards.map((c, i) => (
          <motion.div
            key={c.pain}
            {...fadeUp(0.05 + i * 0.06)}
            className="rounded-2xl border border-[#E2E8F0] dark:border-white/10 bg-white dark:bg-white/[0.04] p-6 sm:p-7 hover:border-[#0F62FE]/30 dark:hover:border-[#0F62FE]/50 hover:-translate-y-0.5 transition-all"
            data-testid={`trust-marketing-why-card-${i}`}
          >
            <c.icon className="w-5 h-5 text-[#0F62FE]" strokeWidth={2.25} />
            <div className="mt-4 text-[17px] font-semibold text-[#0A0A0A] dark:text-[#F5F5F5] leading-snug">{c.pain}</div>
            <p className="mt-2 text-[14px] text-[#475569] dark:text-white/70 leading-relaxed">{c.body}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

// =============================================================================
// §9 — INTEGRATIONS (no auto-sync claims)
// =============================================================================
const Integrations = () => (
  <Section id="integrations" className="border-t border-[#E2E8F0] dark:border-white/10">
    <motion.div {...fadeUp(0)} className="max-w-3xl">
      <Eyebrow>Connected</Eyebrow>
      <H2 className="mt-3 max-w-[22ch]">Connects to the systems your program already runs on.</H2>
      <Sub>
        Nova plugs into the tools your compliance and security teams already use. Publish only
        what you choose — nothing leaves the platform without your review.
      </Sub>
    </motion.div>
    <motion.div
      {...fadeUp(0.1)}
      className="mt-10 flex flex-wrap gap-3"
      data-testid="trust-marketing-integrations-tiles"
    >
      {[
        { name: 'GitHub', letter: 'G' },
        { name: 'Google Workspace', letter: 'G' },
        { name: 'AWS', letter: 'A' },
        { name: 'Azure', letter: 'A' },
      ].map((s) => (
        <div
          key={s.name}
          className="flex items-center gap-2.5 rounded-xl border border-[#E2E8F0] dark:border-white/10 bg-white dark:bg-white/[0.04] px-4 py-2.5"
          data-testid={`trust-marketing-integration-${s.name.toLowerCase().replace(/\s/g, '-')}`}
        >
          <div className="w-7 h-7 rounded-md bg-[#0A0A0A] dark:bg-white/10 text-white inline-flex items-center justify-center text-[11px] font-bold">
            {s.letter}
          </div>
          <div className="text-[13.5px] font-semibold text-[#0A0A0A] dark:text-[#F5F5F5]">{s.name}</div>
        </div>
      ))}
    </motion.div>
  </Section>
);

// =============================================================================
// §10 (former Product Gallery — removed; screenshots live in Live Product panel)
// =============================================================================


// =============================================================================
// §11 — FINAL CTA
// =============================================================================
const FinalCTA = () => {
  const { open: openBookDemo } = useBookDemo();
  const navigate = useNavigate();
  return (
    <section
      className="bg-[#0A0A0A] dark:bg-[#141414] py-24 md:py-32 px-6 text-center border-t border-white/10"
      data-testid="trust-marketing-final-cta"
    >
      <div className="max-w-4xl mx-auto">
        <h2 className="font-display font-semibold text-3xl md:text-4xl text-[#FAFAFA] mb-10 leading-[1.1] tracking-tight">
          Turn compliance into a competitive advantage.
        </h2>
        <p className="font-body text-base md:text-lg text-white/70 mb-12 max-w-2xl mx-auto leading-relaxed">
          Give buyers one page. Give your team back their week.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => openBookDemo('final-cta')}
            data-testid="trust-marketing-final-cta-primary"
            className="font-label bg-[#0F62FE] dark:bg-[#2E79FF] text-white hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full shadow-sm hover:shadow-md transition-all duration-500"
          >
            Book a Demo
          </button>
          <button
            type="button"
            onClick={() => navigate('/contact')}
            data-testid="trust-marketing-final-cta-secondary"
            className="font-label border border-white/25 bg-transparent text-[#FAFAFA] hover:bg-[#FAFAFA] dark:hover:bg-[#050505] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full transition-all duration-500"
          >
            Contact Sales
          </button>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// Sticky in-page sub-nav (desktop only, quiet)
// =============================================================================
const StickySubNav = () => {
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState('product');
  const { open: openBookDemo } = useBookDemo();
  useEffect(() => {
    const hero = document.getElementById('hero');
    if (!hero) return;
    const io = new IntersectionObserver(
      ([e]) => setVisible(!e.isIntersecting),
      { threshold: 0.02 },
    );
    io.observe(hero);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const sections = ['product', 'why-nova', 'features', 'ai-governance', 'build', 'integrations'];
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: '-40% 0px -55% 0px' },
    );
    sections.forEach((id) => { const el = document.getElementById(id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, []);

  return (
    <div
      aria-hidden={!visible}
      className={`hidden lg:block fixed top-4 left-1/2 -translate-x-1/2 z-30 transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
      }`}
      data-testid="trust-marketing-subnav"
    >
      <nav className="flex items-center gap-1 bg-white/85 dark:bg-[#0A0A0A]/80 backdrop-blur-md border border-[#E2E8F0] dark:border-white/10 rounded-full shadow-[0_10px_30px_-12px_rgba(15,23,42,0.14)] dark:shadow-[0_10px_30px_-12px_rgba(0,0,0,0.6)] px-2 py-1.5">
        {[
          ['product', 'Product'],
          ['why-nova', 'Why Nova'],
          ['features', 'Features'],
          ['ai-governance', 'AI Governance'],
        ].map(([id, label]) => (
          <a
            key={id}
            href={`#${id}`}
            className={`h-8 px-3 rounded-full text-[12px] font-semibold inline-flex items-center transition-colors ${
              active === id
                ? 'bg-[#0A0A0A] text-white dark:bg-[#F5F5F5] dark:text-[#0A0A0A]'
                : 'text-[#475569] dark:text-white/70 hover:text-[#0A0A0A] dark:hover:text-white hover:bg-[#F1F5F9] dark:hover:bg-white/[0.06]'
            }`}
            data-testid={`trust-marketing-subnav-${id}`}
          >
            {label}
          </a>
        ))}
        <button
          type="button"
          onClick={() => openBookDemo('subnav')}
          className="ml-1 h-8 px-3 rounded-full text-[12px] font-semibold bg-[#0F62FE] text-white hover:bg-[#0A4FCC] dark:bg-[#2E79FF] dark:hover:bg-[#5C97FF]"
          data-testid="trust-marketing-subnav-book-demo"
        >
          Book a demo
        </button>
      </nav>
    </div>
  );
};

// =============================================================================
// Page
// =============================================================================
const TrustMarketingPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSource, setModalSource] = useState('trust_marketing_page');
  const ctxValue = {
    open: (source = 'trust_marketing_page') => {
      setModalSource(source);
      setModalOpen(true);
    },
    close: () => setModalOpen(false),
  };
  useEffect(() => {
    document.title = 'Nova Trust Center — Build trust before your prospects have to ask';
    const desc = 'Create a branded Trust Center where prospects explore your compliance, security, privacy, AI governance, documents, subprocessors, and FAQs — all in one place.';
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); return m;
    })();
    meta.setAttribute('content', desc);
  }, []);
  return (
    <BookDemoCtx.Provider value={ctxValue}>
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] font-body" data-testid="trust-marketing-page">
        <Header />
        <main>
          <Hero />
          <LiveProductPanel />
          <WhyNova />
          <FinalCTA />
        </main>
        <Footer />
        <BookDemoModal open={modalOpen} onClose={() => setModalOpen(false)} source={`trust_marketing_${modalSource}`} />
      </div>
    </BookDemoCtx.Provider>
  );
};

export default TrustMarketingPage;
